import { create } from "zustand";
import { persist } from "zustand/middleware";

import { SHIPPING } from "../config/constants";
import { warmupProduct } from "../services/productDetailsService";
import { ProductDetailsResponse } from "../types/products";
import { trackAddToCart } from "../utils/metaPixel";
import {
  normalizeBackendPrice,
  normalizeRegularPriceWithContext,
} from "../utils/priceUtils";
import {
  extractCategoryFromProduct,
  extractPackageDimensionsFromProduct,
} from "../utils/productUtils";

export interface CartItem {
  cartItemId: string; // Unique key: product_id or product_id::offerId/sellerId for multi-seller
  product: ProductDetailsResponse;
  quantity: number;
  addedAt: number;
  shippingCost?: number; // Optional - our cargo shipping (US→TJ)
  offerShippingFee?: number; // Seller's internal shipping fee (from offers API)
  sellerName?: string; // e.g. "Amazon.com", "GRACE BY GRACE"
  sellerId?: string; // Zinc seller ID
  offerId?: string; // Zinc offer ID
  shippingCalculated: boolean; // Flag to track if shipping was calculated
  shippingCalculationMethod?: string; // How shipping was calculated
  shippingNotes?: string; // Additional shipping information
}

/** Generate a unique cart item ID from product_id + seller info */
export function getCartItemId(
  productId: string,
  sellerInfo?: { sellerName?: string; sellerId?: string; offerId?: string }
): string {
  if (sellerInfo?.offerId) return `${productId}::${sellerInfo.offerId}`;
  if (sellerInfo?.sellerId) return `${productId}::${sellerInfo.sellerId}`;
  return productId;
}

/** Result of adjusted shipping calculation with minimum fee applied */
export interface AdjustedShippingResult {
  /** Original calculated shipping cost */
  originalAmount: number;
  /** Adjusted amount (with minimum fee applied if needed) */
  adjustedAmount: number;
  /** Whether the minimum fee was applied */
  minimumApplied: boolean;
  /** The minimum fee threshold */
  minimumFee: number;
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  isCalculatingShipping: boolean; // Loading state for shipping calculation

  // Actions
  addToCart: (
    product: ProductDetailsResponse,
    quantity?: number,
    shippingCost?: number,
    offersPrice?: number,
    offerShippingFee?: number,
    sellerInfo?: { sellerName?: string; sellerId?: string; offerId?: string }
  ) => void;
  removeFromCart: (cartItemId: string) => void;
  removeSelectedItems: (cartItemIds: string[]) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;

  // Computed values
  getItemCount: () => number;
  getTotalPrice: () => number;
  getItemQuantity: (cartItemId: string) => number;
  isInCart: (cartItemId: string) => boolean;

  // Shipping-related methods
  getItemShippingCost: (cartItemId: string) => number;
  getTotalShippingCost: () => number;
  getAdjustedShippingCost: () => AdjustedShippingResult;
  getItemTotalWithShipping: (cartItemId: string) => number;
  getTotalWithShipping: () => number;

  // New shipping calculation methods
  calculateMissingShipping: () => Promise<void>;
  updateItemShipping: (
    cartItemId: string,
    shippingCost: number,
    method: string,
    notes?: string
  ) => void;
  getItemsNeedingShipping: () => CartItem[];
  hasItemsNeedingShipping: () => boolean;

  // Price update methods
  updateItemPrice: (
    cartItemId: string,
    newPrice: number,
    newShippingCost?: number
  ) => void;
  updatePricesFromValidation: (
    updates: Array<{
      productId: string;
      newPrice: number;
      newShippingCost: number;
    }>
  ) => void;
}

interface PersistedCartState {
  items: CartItem[];
  timestamp: number;
}

const normalizeCartPrice = (
  price: number | string | null | undefined,
  listPrice?: number | string | null | undefined
): number => {
  const normalized =
    normalizeRegularPriceWithContext(price, listPrice) ??
    normalizeBackendPrice(price, "list");

  if (normalized !== null) {
    return normalized;
  }

  if (typeof price === "string") {
    const parsed = parseFloat(price);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return price ?? 0;
};

/**
 * Warmup product data in the background and update cart if price changes.
 * This runs asynchronously and doesn't block the UI.
 */
const warmupProductInBackground = async (
  productId: string,
  retailer: string,
  fallbackPrice: number,
  getState: () => CartState
): Promise<void> => {
  try {
    const result = await warmupProduct(productId, retailer);

    if (result.priceChanged && result.currentPrice != null) {
      getState().updateItemPrice(
        productId,
        result.currentPrice,
        result.shippingCost ?? undefined
      );
    }
  } catch (error) {
    // Warmup failed - not critical, cart still works
    console.warn(`[Cart] Warmup failed for ${productId}:`, error);
  }
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      isCalculatingShipping: false,

      addToCart: (
        product: ProductDetailsResponse,
        quantity = 1,
        shippingCost?: number,
        offersPrice?: number,
        offerShippingFee?: number,
        sellerInfo?: { sellerName?: string; sellerId?: string; offerId?: string }
      ) => {
        const productId = product.product_id;
        const cartItemId = getCartItemId(productId, sellerInfo);
        const isNewItem = !get().isInCart(cartItemId);
        const listPriceContext =
          product.original_retail_price ?? product.price;
        // Use offers price (accurate) if provided, otherwise normalize details price
        const normalizedPrice =
          offersPrice ?? normalizeCartPrice(product.price, listPriceContext);
        const normalizedProduct: ProductDetailsResponse = {
          ...product,
          price: normalizedPrice,
        };

        set((state) => {
          // Match by cartItemId (product_id + seller) so same product from different sellers = separate items
          const existingItemIndex = state.items.findIndex(
            (item) => item.cartItemId === cartItemId
          );

          if (existingItemIndex >= 0) {
            // Update existing item quantity (same product + same seller)
            const updatedItems = [...state.items];
            const existingItem = updatedItems[existingItemIndex];
            updatedItems[existingItemIndex] = {
              ...existingItem,
              product: {
                ...existingItem.product,
                ...normalizedProduct,
                price: normalizedPrice,
              },
              quantity: existingItem.quantity + quantity,
              ...(shippingCost !== undefined && {
                shippingCost,
                shippingCalculated: true,
                shippingCalculationMethod: "Pre-calculated",
                shippingNotes: "Added from product details page",
              }),
              ...(offerShippingFee !== undefined && {
                offerShippingFee,
              }),
              ...(sellerInfo?.sellerName && { sellerName: sellerInfo.sellerName }),
              ...(sellerInfo?.sellerId && { sellerId: sellerInfo.sellerId }),
              ...(sellerInfo?.offerId && { offerId: sellerInfo.offerId }),
            };
            return { items: updatedItems };
          } else {
            // Add new item (new product or same product from different seller)
            const newItem: CartItem = {
              cartItemId,
              product: normalizedProduct,
              quantity,
              addedAt: Date.now(),
              shippingCost: shippingCost,
              offerShippingFee: offerShippingFee,
              sellerName: sellerInfo?.sellerName,
              sellerId: sellerInfo?.sellerId,
              offerId: sellerInfo?.offerId,
              shippingCalculated: shippingCost !== undefined,
              shippingCalculationMethod:
                shippingCost !== undefined ? "Pre-calculated" : undefined,
              shippingNotes:
                shippingCost !== undefined
                  ? "Added from product details page"
                  : undefined,
            };

            return { items: [...state.items, newItem] };
          }
        });

        trackAddToCart({
          contentId: normalizedProduct.product_id || normalizedProduct.asin || "",
          contentName: normalizedProduct.title,
          contentCategory:
            normalizedProduct.categories?.[
              normalizedProduct.categories.length - 1
            ] || normalizedProduct.brand,
          value: normalizedPrice * quantity,
          currency: normalizedProduct.currency || "USD",
          quantity,
        });

        // Fire warmup in background for new items (fire-and-forget)
        if (isNewItem) {
          warmupProductInBackground(
            productId,
            product.retailer || "amazon",
            product.price ?? 0,
            get
          );
        }
      },

      removeFromCart: (cartItemId: string) => {
        set((state) => ({
          items: state.items.filter(
            (item) => item.cartItemId !== cartItemId
          ),
        }));
      },

      removeSelectedItems: (cartItemIds: string[]) => {
        set((state) => ({
          items: state.items.filter(
            (item) => !cartItemIds.includes(item.cartItemId)
          ),
        }));
      },

      updateQuantity: (cartItemId: string, quantity: number) => {
        if (quantity <= 0) {
          get().removeFromCart(cartItemId);
          return;
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.cartItemId === cartItemId
              ? {
                  ...item,
                  quantity,
                }
              : item
          ),
        }));
      },

      clearCart: () => {
        set({ items: [] });
      },

      toggleCart: () => {
        set((state) => ({ isOpen: !state.isOpen }));
      },

      openCart: () => {
        set({ isOpen: true });
      },

      closeCart: () => {
        set({ isOpen: false });
      },

      // Computed values
      getItemCount: () => {
        const state = get();
        return state.items.reduce((total, item) => total + item.quantity, 0);
      },

      getTotalPrice: () => {
        const state = get();
        return state.items.reduce((total, item) => {
          const price = item.product.price || 0;
          // Include seller's internal shipping fee (per item, not per quantity)
          const sellerShipping = item.offerShippingFee || 0;
          return total + (price * item.quantity) + sellerShipping;
        }, 0);
      },

      getItemQuantity: (cartItemId: string) => {
        const state = get();
        // Match by cartItemId first, fallback to product_id for backward compat
        const item = state.items.find(
          (item) => item.cartItemId === cartItemId || item.product.product_id === cartItemId
        );
        return item ? item.quantity : 0;
      },

      isInCart: (cartItemId: string) => {
        const state = get();
        // Match by cartItemId first, fallback to product_id for backward compat
        return state.items.some(
          (item) => item.cartItemId === cartItemId || item.product.product_id === cartItemId
        );
      },

      // Shipping-related methods
      getItemShippingCost: (cartItemId: string) => {
        const state = get();
        const item = state.items.find(
          (item) => item.cartItemId === cartItemId || item.product.product_id === cartItemId
        );
        if (!item) return 0;

        // Return calculated shipping cost if available
        if (item.shippingCalculated && item.shippingCost !== undefined) {
          return item.shippingCost;
        }

        // Check if product has free shipping
        if (item.product.free_shipping) return 0;

        // Use individual shipping price if available, otherwise 0
        return item.product.ship_price ?? 0;
      },

      getTotalShippingCost: () => {
        const state = get();
        return state.items.reduce((total, item) => {
          // Check if product has free shipping
          if (item.product.free_shipping) return total;

          // Use calculated shipping cost if available
          if (item.shippingCalculated && item.shippingCost !== undefined) {
            return total + item.shippingCost * item.quantity;
          }

          // Use individual shipping price if available, otherwise 0
          const shippingCost = item.product.ship_price ?? 0;
          return total + shippingCost * item.quantity;
        }, 0);
      },

      getAdjustedShippingCost: (): AdjustedShippingResult => {
        const originalAmount = get().getTotalShippingCost();
        const minimumFee = SHIPPING.MINIMUM_FEE;
        const minimumApplied = originalAmount < minimumFee && originalAmount > 0;

        return {
          originalAmount,
          adjustedAmount: minimumApplied ? minimumFee : originalAmount,
          minimumApplied,
          minimumFee,
        };
      },

      getItemTotalWithShipping: (cartItemId: string) => {
        const state = get();
        const item = state.items.find(
          (item) => item.cartItemId === cartItemId || item.product.product_id === cartItemId
        );
        if (!item) return 0;

        const productTotal = (item.product.price || 0) * item.quantity;
        const sellerShipping = item.offerShippingFee || 0;
        const cargoShipping = get().getItemShippingCost(item.cartItemId);

        return productTotal + sellerShipping + cargoShipping;
      },

      getTotalWithShipping: () => {
        const state = get();
        const subtotal = get().getTotalPrice();
        const shippingCost = get().getTotalShippingCost();
        return subtotal + shippingCost;
      },

      // New shipping calculation methods
      calculateMissingShipping: async () => {
        const state = get();

        // Guard: Prevent duplicate calls if already calculating
        if (state.isCalculatingShipping) {
          return;
        }

        const itemsNeedingShipping = state.items.filter(
          (item) => !item.shippingCalculated
        );

        if (itemsNeedingShipping.length === 0) {
          return;
        }

        set({ isCalculatingShipping: true });

        try {
          // Import shipping service dynamically to avoid circular dependencies
          const { shippingService } = await import(
            "../services/shippingService"
          );

          for (const item of itemsNeedingShipping) {
            try {
              // Extract category and package dimensions from product data
              const category = extractCategoryFromProduct(item.product);
              const packageDimensions = extractPackageDimensionsFromProduct(
                item.product
              );

              const shippingResult = await shippingService.calculateShipping({
                productId: item.product.product_id,
                retailer: item.product.retailer || "amazon",
                category,
                categories: item.product.categories || [],
                packageDimensions: packageDimensions || undefined,
                productTitle: item.product.title,
              });

              get().updateItemShipping(
                item.cartItemId,
                shippingResult.shippingCost,
                shippingResult.calculationMethod,
                shippingResult.notes
              );
            } catch (error) {
              // Don't set shipping cost on error - leave it uncalculated
              // The item will remain with shippingCalculated = false
            }
          }
        } catch (error) {
          // Silently handle shipping calculation errors
        } finally {
          set({ isCalculatingShipping: false });
        }
      },

      updateItemShipping: (
        cartItemId: string,
        shippingCost: number,
        method: string,
        notes?: string
      ) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.cartItemId === cartItemId
              ? {
                  ...item,
                  shippingCost,
                  shippingCalculated: true,
                  shippingCalculationMethod: method,
                  shippingNotes: notes,
                }
              : item
          ),
        }));
      },

      getItemsNeedingShipping: () => {
        const state = get();
        return state.items.filter((item) => !item.shippingCalculated);
      },

      hasItemsNeedingShipping: () => {
        const state = get();
        return state.items.some((item) => !item.shippingCalculated);
      },

      // Price update methods
      updateItemPrice: (
        cartItemId: string,
        newPrice: number,
        newShippingCost?: number
      ) => {
        set((state) => ({
          items: state.items.map((item) =>
            (item.cartItemId === cartItemId || item.product.product_id === cartItemId)
              ? {
                  ...item,
                  product: {
                    ...item.product,
                    price: normalizeCartPrice(
                      newPrice,
                      item.product.original_retail_price ?? item.product.price
                    ),
                  },
                  ...(newShippingCost !== undefined && {
                    shippingCost: newShippingCost,
                    shippingCalculated: true,
                  }),
                }
              : item
          ),
        }));
      },

      updatePricesFromValidation: (
        updates: Array<{
          productId: string;
          newPrice: number;
          newShippingCost: number;
        }>
      ) => {
        set((state) => ({
          items: state.items.map((item) => {
            const update = updates.find(
              (u) => u.productId === item.product.product_id
            );
            if (update) {
              return {
                ...item,
                product: {
                  ...item.product,
                  price: normalizeCartPrice(
                    update.newPrice,
                    item.product.original_retail_price ?? item.product.price
                  ),
                },
                shippingCost: update.newShippingCost,
                shippingCalculated: true,
              };
            }
            return item;
          }),
        }));
      },
    }),
    {
      name: "cart-storage",
      partialize: (state) => ({
        items: state.items,
        timestamp: Date.now(), // Save when cart was last updated
      }),
      // Rehydrate storage when page loads
      onRehydrateStorage: () => (state) => {
        if (!state || !state.items) return;

        const now = Date.now();
        const stored = (state as any).timestamp || now;
        const age = now - stored;
        const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

        // Migrate old items that don't have cartItemId
        const migratedItems = state.items.map((item) => ({
          ...item,
          cartItemId: item.cartItemId || getCartItemId(
            item.product.product_id,
            { offerId: item.offerId, sellerId: item.sellerId }
          ),
        }));

        // If cart is older than 24 hours
        if (age > TWENTY_FOUR_HOURS) {
          // Keep items but clear shipping data (might be outdated)
          const freshItems = migratedItems.map((item) => ({
            ...item,
            shippingCost: undefined,
            shippingCalculated: false, // Force recalculation with current prices
            shippingCalculationMethod: undefined,
            shippingNotes: undefined,
          }));

          return {
            ...state,
            items: freshItems,
            timestamp: now,
          };
        }

        return {
          ...state,
          items: migratedItems,
        };
      },
    }
  )
);
